import React, { useRef, useState } from 'react';
import './AdPreview.pcss';
import { useSelector } from 'react-redux';
import playImage from 'assets/fbPlayButton.png';
import everyone from 'assets/fbEveryone.png';
import messenger from 'assets/fbMessengerGrey.png';
import noImage from 'assets/no-image.png';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';

type Props = {
  thumbUrl: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  primaryText?: string;
  headline?: string;
  callToAction?: string;
};

const minRatio = 1 / 1.91;

const AdImagePreview = ({ imageUrl }: { imageUrl: string }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [height, setHeight] = useState(180);
  return (
    <div className="ad-preview__media-container" style={{ height }}>
      <img
        onLoad={() => {
          const image = imageRef.current;
          const ratio = Math.max(
            image ? image.naturalHeight / image.naturalWidth : 0,
            minRatio,
          );
          setHeight(Math.min(322, 320 * ratio + 2));
        }}
        key={imageUrl}
        ref={imageRef}
        src={imageUrl}
        className="ad-preview__image-dimensions"
      />
      <div
        className="ad-preview__image"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
    </div>
  );
};

const AdVideoPreview = ({
  videoUrl,
  thumbUrl,
}: {
  videoUrl: string;
  thumbUrl: string | null;
}) => {
  const [clicked, setClicked] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const play = () => {
    if (ref.current !== null) {
      ref.current.play();
      setClicked(true);
    }
  };
  return (
    <div className="ad-preview__media-container">
      <video
        width="320"
        className="ad-preview__video"
        src={videoUrl}
        controls={clicked}
        ref={ref}
      />
      {!clicked && (
        <div
          className="ad-preview__video-play"
          onClick={play}
          style={{ backgroundImage: thumbUrl ? `url(${thumbUrl})` : 'none' }}
        >
          <img src={playImage} className="ad-preview__video-play-image" />
        </div>
      )}
    </div>
  );
};

export const AdPreview = (props: Props) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const {
    thumbUrl,
    videoUrl,
    imageUrl,
    callToAction = 'Send Message',
    primaryText = 'Primary Text',
    headline = 'Connect in Messenger',
  } = props;
  return (
    <div className="ad-preview">
      <div className="ad-preview__header">
        <img
          alt="Facebook Page Profile"
          className="ad-preview__profile-image"
          src={facebookPage?.squarePicture}
        />
        <div className="ad-preview__page-info">
          <div>
            <div className="ad-preview__page-name">{facebookPage?.name}</div>
            <div className="ad-preview__sponsored-ad">
              <span>Sponsored · </span>
              <img src={everyone} className="ad-preview__sponsored-image" />
            </div>
          </div>
          <div className="ad-preview__more" />
        </div>
      </div>
      <div className="ad-preview__primary-text">{primaryText}</div>
      {!imageUrl && !videoUrl && (
        <div className="ad-preview__no-image-container">
          <img src={noImage} className="ad-preview__no-image" />
        </div>
      )}
      {imageUrl && <AdImagePreview imageUrl={imageUrl} />}
      {videoUrl && <AdVideoPreview videoUrl={videoUrl} thumbUrl={thumbUrl} />}
      <div className="ad-preview__footer">
        <div className="ad-preview__headline-container">
          <div className="ad-preview__messenger">Messenger</div>
          <div className="ad-preview__headline">{headline}</div>
        </div>
        <div className="ad-preview__send-message">
          <img src={messenger} className="ad-preview__send-message-icon" />
          <span>{callToAction}</span>
        </div>
      </div>
      <hr className="ad-preview__actions-separator" />
      <div className="ad-preview__fb-actions">
        <div className="ad-preview__fb-action">
          <div className="ad-preview__fb-action-icon ad-preview__fb-action-icon-like" />
          Like
        </div>
        <div className="ad-preview__fb-action">
          <div className="ad-preview__fb-action-icon ad-preview__fb-action-icon-comment" />
          Comment
        </div>
        <div className="ad-preview__fb-action">
          <div className="ad-preview__fb-action-icon ad-preview__fb-action-icon-share" />
          Share
        </div>
      </div>
    </div>
  );
};
